import { Row } from '@tanstack/react-table';
import { Controller, UseFormReturn } from 'react-hook-form';

import { Input } from '@/components/ui/input';
import i18n from '@/core/i18n';
import { DecimalFormField, InputFieldWithCurrency } from '@/modules/common';
import {
  FINANCE_MAX_DECIMAL_PLACES,
  FINANCE_MAX_INTEGER_DIGITS,
  FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES,
} from '@/modules/finance/common/constants/rounding.constants';

import { useHasZeroAmountError } from '../../../hooks/generate-bills/use-has-zero-amount-error';
import { GenerateBillListItem } from '../../../types';
import { GenerateBillsTableFormValues } from '../../../validations';
import { getBillFieldName, isReadOnly } from '../bill-row.helpers';

interface InputCellProps {
  row: Row<GenerateBillListItem>;
  form: UseFormReturn<GenerateBillsTableFormValues>;
}

/** Hours input for a sub-row, bordered red when the line would bill nothing. */
export const ProjectHoursInput = ({ row, form }: InputCellProps) => {
  const hasZeroError = useHasZeroAmountError(row);

  return (
    <DecimalFormField
      control={form.control}
      name={getBillFieldName(row, 'hours')}
      disabled={isReadOnly(row)}
      className={hasZeroError ? 'border-destructive' : ''}
      maxDecimalPlaces={FINANCE_MAX_DECIMAL_PLACES}
      maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
    />
  );
};

interface OverheadDescriptionInputProps extends InputCellProps {
  defaultValue: string;
}

/**
 * Free-text description for a user-added overhead line.
 *
 * Wraps `Controller` here rather than in the column definition because the record-keyed form
 * schema makes RHF resolve a dynamic path's value to a union of every possible leaf — narrowing it
 * to `string` for the `Input` belongs next to the input, not scattered through the columns file.
 */
export const OverheadDescriptionInput = ({
  row,
  form,
  defaultValue,
}: OverheadDescriptionInputProps) => (
  <Controller
    control={form.control}
    name={getBillFieldName(row, 'description')}
    defaultValue={defaultValue as never}
    render={({ field }) => (
      <Input
        {...field}
        value={(field.value as string | undefined) ?? ''}
        placeholder={i18n.t('authenticated.bills:new.overhead.descriptionPlaceholder')}
        className="min-w-0 flex-1 text-sm"
      />
    )}
  />
);

/** Rate input for a sub-row, bordered red when the line would bill nothing. */
export const ProjectRateCell = ({ row, form }: InputCellProps) => {
  const hasZeroError = useHasZeroAmountError(row);

  return (
    <div className={hasZeroError ? '[&_input]:border-destructive' : ''}>
      <InputFieldWithCurrency
        name={getBillFieldName(row, 'effectiveRate')}
        isReadMode={isReadOnly(row)}
        control={form.control}
        suffixText={i18n.t('common:rateUnits.perHour', {
          currency: row.original.currency.code,
        })}
        maxDecimalPlaces={FINANCE_ROUNDING_EFFECTIVE_RATE_DECIMAL_PLACES}
        maxIntegerDigits={FINANCE_MAX_INTEGER_DIGITS}
      />
    </div>
  );
};
